import React, { useEffect, useMemo, useRef, useState } from "react";
import TailwindBox, { TailwindBoxProps } from "../box";
import {
  TBackgroundColor,
  TBorderColor,
  TBorderRadius,
  TTextColor,
} from "../../../styles/tailwind-classnames";
import { TailwindCardBackgroundColor } from "../card";
import { inject, observer } from "mobx-react";
import useOutsideClick from "../../../core/utility/hooks/useOutsideClick";
import ThemeStore from "../../../core/stores/theme/ThemeStore";
import { TailwindDropdownBackgroundColor } from "../dropdown";
import { MODAL_WRAPPER_CLASSNAME } from "../../../core/stores/modal/consts";

export type TailwindMenuProps = Omit<TailwindBoxProps, "backgroundColor"> & {
  backgroundColor?: TailwindDropdownBackgroundColor;
  isCollapsed: boolean;
  setIsCollapsed: (isCollapsed: boolean) => void;
  shape?: "rounded" | "square";
  buttonRef: React.RefObject<HTMLElement>;
  themeStore?: ThemeStore;
  fullWidth?: boolean;
  isShowingMenuOnTop: boolean;
  direction?: "top" | "bottom";
  setIsShowingMenuOnTop: (isOnTop: boolean) => void;
};

const TailwindMenu: React.ComponentType<TailwindMenuProps> = ({
  isCollapsed,
  backgroundColor,
  setIsCollapsed,
  shape,
  buttonRef,
  themeStore,
  fullWidth = true,
  isShowingMenuOnTop: _isShowingMenuOnTop,
  setIsShowingMenuOnTop,
  maxHeight,
  width,
  borderColor,
  borderWidth,
  direction,
  children,
}) => {
  /*
   ###### Component Flows
   Component needs to work in following cases:
   - If inside a modal and modal is small, it should decide the position and the height of the menu
   - if inside a non-modal container and container is small, it should decide the position and the height of the menu
   ###: Note that on both cases, there can be a scroll in the modal/container. the visible viewport should be taken into account
   so that user does not need to scroll down to see the rest of the options. the height/position of the menu should adjust accordingly
   ###: Another note is that if height of the menu is small (like three items) and there is more available space than needed, height of the menu should be the auto-height so that the menu does not occupy space more than needed
   */

  const { currentThemePalette } = themeStore!;

  const menuRef = useRef<HTMLDivElement>(null);
  const [menuHeight, setMenuHeight] = useState<number | "auto">("auto");
  const [menuBorderRadius, setMenuBorderRadius] = useState<TBorderRadius[]>(
    shape === "rounded"
      ? ["rounded-bl-3xl", "rounded-br-3xl"]
      : ["rounded-bl", "rounded-br"]
  );
  const [menuTop, setMenuTop] = useState<number | null>(null);
  const [menuBottom, setMenuBottom] = useState<number | null>(null);

  const buttonEl = buttonRef.current;
  const naturalMenuHeight = (React.Children.count(children) - 1) * 40 + 22;
  const maxMenuHeight = 384;
  const VIEWPORT_MARGIN = 16;
  // Menus whose caller-supplied CSS max-height is at or below this value are
  // treated as "short" -- the caller is explicitly using a fixed-height
  // scrolling container (e.g. ProductDropdown's max-h-32) and would rather
  // overflow the surrounding card / viewport than be shrunk to a few pixels.
  // Default max-h-96 (384) menus stay "tall" and keep BCAMS-4331's viewport
  // clamp + auto-flip behavior so camera/microphone lists with 10+ items
  // don't render past the bottom of the screen with an unreachable scroller.
  const SHORT_MENU_THRESHOLD = 200;

  const getCssMaxHeight = (): number => {
    if (!menuRef.current) return maxMenuHeight;
    const raw = parseFloat(getComputedStyle(menuRef.current).maxHeight);
    return !isNaN(raw) && raw > 0 ? raw : maxMenuHeight;
  };

  const getModalBottom = () => {
    const isAnyModalOpen: boolean =
      document.querySelector(`.${MODAL_WRAPPER_CLASSNAME}`) !== null;
    const buttonEl = buttonRef?.current;
    let modalBottom: number | null = null;
    if (isAnyModalOpen) {
      const openModals = document.querySelectorAll(
        `.${MODAL_WRAPPER_CLASSNAME}`
      );

      openModals.forEach(modal => {
        if (modal.contains(buttonEl)) {
          const { bottom: _modalBottom } = modal.getBoundingClientRect();
          modalBottom = _modalBottom;
        }
      });
    }

    return modalBottom;
  };

  const getModalTop = () => {
    const isAnyModalOpen: boolean =
      document.querySelector(`.${MODAL_WRAPPER_CLASSNAME}`) !== null;
    const buttonEl = buttonRef?.current;
    let modalTop: number | null = null;
    if (isAnyModalOpen) {
      const openModals = document.querySelectorAll(
        `.${MODAL_WRAPPER_CLASSNAME}`
      );

      openModals.forEach(modal => {
        if (modal.contains(buttonEl)) {
          const { top: _modalTop } = modal.getBoundingClientRect();
          modalTop = _modalTop;
        }
      });
    }

    return modalTop;
  };

  const isInsideModal = getModalTop() || getModalBottom();

  const getAvailableSpace = () => {
    let menuHeightWhenOnTop = 0;
    let menuHeightWhenOnBottom = 0;
    if (buttonEl) {
      const { top: buttonTop, bottom: buttonBottom } =
        buttonEl.getBoundingClientRect();
      if (isInsideModal) {
        const modalTop = getModalTop()!;
        const modalBottom = getModalBottom()!;
        menuHeightWhenOnTop = Math.abs(modalTop - buttonTop);
        menuHeightWhenOnBottom = Math.abs(modalBottom - buttonBottom);
      } else {
        const windowInnerHeight = window.innerHeight;
        menuHeightWhenOnTop = Math.abs(buttonTop);
        menuHeightWhenOnBottom = Math.abs(windowInnerHeight - buttonBottom);
      }
    }

    return { menuHeightWhenOnTop, menuHeightWhenOnBottom };
  };

  const getMenuHeight = (
    renderOnTop: boolean,
    spaceAbove: number,
    spaceBelow: number,
    cssMaxHeight: number
  ) => {
    const cappedNatural = Math.min(naturalMenuHeight, cssMaxHeight);
    // Short menus (caller-supplied small max-height): render at the
    // caller-intended size and accept viewport / card overflow. Shrinking
    // them by a few-px directional space makes them unusable on small
    // viewports (e.g. broadcast Show Modes pricing dropdowns).
    if (cssMaxHeight <= SHORT_MENU_THRESHOLD) {
      return cappedNatural;
    }
    // Tall menus: clamp to the chosen direction's available space so the
    // inner scroller stays reachable on the visible viewport (BCAMS-4331
    // camera/microphone select with 10+ devices).
    const directionalSpace = Math.max(
      0,
      (renderOnTop ? spaceAbove : spaceBelow) - VIEWPORT_MARGIN
    );
    return Math.min(cappedNatural, directionalSpace);
  };

  const _setIsCollapsed = (_isCollapsed: boolean) => {
    setIsCollapsed(_isCollapsed);
  };

  useOutsideClick(
    menuRef,
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      _setIsCollapsed(true);
    },
    buttonRef
  );

  // do not add any react hooks
  const getMenuPositionProps = (_isShowingMenuOnTop: boolean) => {
    let borderRadius: TBorderRadius[] = menuBorderRadius;
    let top: number | null = null;
    let bottom: number | null = null;

    if (buttonEl) {
      const { height: buttonHeight } = buttonEl.getBoundingClientRect();
      if (_isShowingMenuOnTop) {
        borderRadius =
          shape === "rounded"
            ? ["rounded-tl-3xl", "rounded-tr-3xl"]
            : ["rounded-tl", "rounded-tr"];
        bottom = buttonHeight;
      } else {
        top = buttonHeight;
      }
    }

    return {
      borderRadius,
      top,
      bottom,
    };
  };

  const themeColors = useMemo(() => {
    const colors: {
      backgroundColor: TBackgroundColor;
      focusButtonBorderColor: TBorderColor;
      multipleCounterBackgroundColor: TBackgroundColor;
      multipleCounterTextColor: TTextColor;
    } = {
      backgroundColor: "bg-white",
      focusButtonBorderColor: "border-black",
      multipleCounterBackgroundColor: "bg-black",
      multipleCounterTextColor: "text-white",
    };

    switch (backgroundColor as TailwindCardBackgroundColor) {
      case "primary": {
        const backgroundClass =
          currentThemePalette?.["dropdown-primary-bg-color-tw-class"];
        if (backgroundClass) {
          colors.backgroundColor = backgroundClass;
        }
        break;
      }
      case "secondary": {
        const backgroundClass =
          currentThemePalette?.["dropdown-secondary-bg-color-tw-class"];
        if (backgroundClass) {
          colors.backgroundColor = backgroundClass;
        }
        break;
      }
      case "transparent": {
        colors.backgroundColor = "bg-transparent";
        break;
      }
      default: {
        colors.backgroundColor = backgroundColor as TBackgroundColor;
        break;
      }
    }

    return colors;
  }, [backgroundColor]);

  useEffect(() => {
    const { menuHeightWhenOnTop, menuHeightWhenOnBottom } = getAvailableSpace();
    const hasMoreSpaceAvailableAtTop =
      menuHeightWhenOnTop > menuHeightWhenOnBottom;
    const cssMaxHeight = getCssMaxHeight();
    const isShortMenu = cssMaxHeight <= SHORT_MENU_THRESHOLD;

    let renderOnTop: boolean;
    if (direction === "top") {
      renderOnTop = true;
    } else if (direction === "bottom") {
      renderOnTop = false;
    } else {
      renderOnTop = hasMoreSpaceAvailableAtTop;
    }

    // For tall menus (default max-h-96) only, auto-flip when the forced side
    // has effectively no room AND the opposite side has meaningfully more
    // space. Short menus (e.g. pricing dropdowns with max-h-32) never
    // auto-flip: their parent cards typically have overflow-hidden, and
    // flipping into that clipped region is worse than letting the menu
    // overflow downward in the caller-requested direction. The directional
    // height clamp inside getMenuHeight prevents the original camera/
    // microphone off-screen overflow (BCAMS-4331) without needing the flip.
    if (direction && !isShortMenu) {
      const MIN_USABLE_HEIGHT = 64;
      const forcedSpace = renderOnTop
        ? menuHeightWhenOnTop
        : menuHeightWhenOnBottom;
      const oppositeSpace = renderOnTop
        ? menuHeightWhenOnBottom
        : menuHeightWhenOnTop;
      if (
        forcedSpace - VIEWPORT_MARGIN < MIN_USABLE_HEIGHT &&
        oppositeSpace > forcedSpace + MIN_USABLE_HEIGHT
      ) {
        renderOnTop = !renderOnTop;
      }
    }

    const height = getMenuHeight(
      renderOnTop,
      menuHeightWhenOnTop,
      menuHeightWhenOnBottom,
      cssMaxHeight
    );
    setMenuHeight(height);
    setIsShowingMenuOnTop(renderOnTop);

    const { borderRadius, top, bottom } = getMenuPositionProps(renderOnTop);
    setMenuBorderRadius(borderRadius);
    setMenuTop(top);
    setMenuBottom(bottom);
  }, [isCollapsed, direction]);

  return (
    <TailwindBox
      ref={menuRef}
      width={fullWidth ? "w-full" : width || "w-52"}
      display={isCollapsed ? "hidden" : "flex"}
      visibility={isCollapsed ? "invisible" : "visible"}
      padding={["p-3"]}
      borderRadius={menuBorderRadius}
      backgroundColor={themeColors.backgroundColor}
      borderColor={borderColor}
      borderWidth={borderWidth}
      position={"absolute"}
      flexDirection={"flex-col"}
      maxHeight={maxHeight || "max-h-96"}
      overflow={"overflow-hidden"}
      zIndex={"z-900"}
      style={{
        top: menuTop === null ? undefined : menuTop,
        bottom: menuBottom === null ? undefined : menuBottom,
        height: menuHeight,
      }}>
      <TailwindBox
        overflow={["overflow-y-auto"]}
        height={"h-full"}
        className={["pretty-scroll"]}>
        {children}
      </TailwindBox>
    </TailwindBox>
  );
};

export default inject("themeStore")(observer(TailwindMenu));
